import type {
  CommandReceipt,
  Role,
  WorkspaceCommand,
  WorkspaceState,
} from "../domain/types";
import { POLICY_VERSION } from "../legal/policy";

interface ApiErrorShape {
  error: {
    code: string;
    message: string;
    retryable: boolean;
    fieldErrors: Record<string, string | string[] | undefined>;
    requestId: string;
  };
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly retryable: boolean,
    public readonly requestId: string,
    public readonly fieldErrors: Record<
      string,
      string | string[] | undefined
    > = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function startSample(): Promise<{
  state: WorkspaceState;
  csrfToken: string;
}> {
  return request("/api/sample", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ acquisition: readPublicAcquisitionContext() }),
  });
}

export async function restoreSession(): Promise<{
  state: WorkspaceState;
  csrfToken: string;
  role: Role | "sample_user";
  mode: "sample" | "user";
}> {
  return request("/api/session");
}

export async function sendCommand(
  state: WorkspaceState,
  csrfToken: string,
  command: WorkspaceCommand,
  idempotencyKey = `web-${crypto.randomUUID()}`,
): Promise<{
  state: WorkspaceState;
  receipt: CommandReceipt;
  replayed: boolean;
}> {
  return request("/api/commands", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-csrf-token": csrfToken,
      "idempotency-key": idempotencyKey,
      "if-match": `"${state.version}"`,
    },
    body: JSON.stringify(command),
  });
}

export async function createGuardianLink(
  state: WorkspaceState,
  csrfToken: string,
  draftId: string,
): Promise<{
  state: WorkspaceState;
  receipt: CommandReceipt;
  url: string;
  expiresAt: string;
  remainingUses: number;
}> {
  return request("/api/guardian-links", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-csrf-token": csrfToken,
      "idempotency-key": `guardian-${crypto.randomUUID()}`,
      "if-match": `"${state.version}"`,
    },
    body: JSON.stringify({ draftId }),
  });
}

export async function readGuardianLink(
  token: string,
): Promise<{
  capabilityId: string;
  purpose: string;
  payload: {
    academyName: string;
    studentLabel: string;
    purpose: string;
    body: string;
    issuedAt: string;
  };
  expiresAt: string;
  remainingUses: number;
  requestId: string;
}> {
  return request(`/api/guardian/${encodeURIComponent(token)}`);
}

export async function register(payload: {
  academyName: string;
  email: string;
  password: string;
  policyVersion: typeof POLICY_VERSION;
}): Promise<{ state: WorkspaceState; csrfToken: string; role: Role }> {
  return request("/api/auth/register", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      ...payload,
      acquisition: readPublicAcquisitionContext(),
    }),
  });
}

export interface PublicConfig {
  signupOpen: boolean;
  publicationStatus: string;
  supportConnected: boolean;
  paymentConnected: boolean;
  messageConnected: boolean;
}

export async function readPublicConfig(): Promise<PublicConfig> {
  return request("/api/public-config");
}

export type PublicAcquisitionEvent =
  | "landing_viewed"
  | "primary_start_clicked";

export interface PublicAcquisitionContext {
  anonymousSessionId: string;
  attribution: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
  };
}

const ACQUISITION_SESSION_KEY = "sueopmachim:acquisition-session:v1";
const ACQUISITION_ATTRIBUTION_KEY = "sueopmachim:acquisition-attribution:v1";
const UTM_VALUE = /^[A-Za-z0-9._~+-]{1,64}$/;
let inMemoryAnonymousSessionId: string | null = null;

function readStoredAttribution(): PublicAcquisitionContext["attribution"] | null {
  try {
    const stored = sessionStorage.getItem(ACQUISITION_ATTRIBUTION_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Record<string, unknown>;
    const attribution: PublicAcquisitionContext["attribution"] = {};
    for (const key of ["source", "medium", "campaign", "content"] as const) {
      const value = parsed[key];
      if (typeof value === "string" && UTM_VALUE.test(value)) attribution[key] = value;
    }
    return attribution;
  } catch {
    return null;
  }
}

function readFirstTouchAttribution(): PublicAcquisitionContext["attribution"] {
  const stored = readStoredAttribution();
  if (stored) return stored;
  const query = new URLSearchParams(window.location.search);
  const attribution: PublicAcquisitionContext["attribution"] = {};
  const mapping = {
    source: "utm_source",
    medium: "utm_medium",
    campaign: "utm_campaign",
    content: "utm_content",
  } as const;
  for (const [key, parameter] of Object.entries(mapping) as Array<
    [keyof PublicAcquisitionContext["attribution"], string]
  >) {
    const value = query.get(parameter)?.trim();
    if (value && UTM_VALUE.test(value)) attribution[key] = value;
  }
  try {
    sessionStorage.setItem(ACQUISITION_ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Storage restrictions never block the product journey.
  }
  return attribution;
}

export function readPublicAcquisitionContext(): PublicAcquisitionContext {
  let anonymousSessionId = inMemoryAnonymousSessionId;
  try {
    anonymousSessionId = sessionStorage.getItem(ACQUISITION_SESSION_KEY);
  } catch {
    // Fall through to an in-memory ID when sessionStorage is unavailable.
  }
  if (!anonymousSessionId) {
    anonymousSessionId = crypto.randomUUID();
    inMemoryAnonymousSessionId = anonymousSessionId;
    try {
      sessionStorage.setItem(ACQUISITION_SESSION_KEY, anonymousSessionId);
    } catch {
      // The request can still carry a random ID for this page lifetime.
    }
  }
  return {
    anonymousSessionId,
    attribution: readFirstTouchAttribution(),
  };
}

export async function recordPublicAcquisitionEvent(
  eventName: PublicAcquisitionEvent,
): Promise<void> {
  const response = await fetch("/api/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      eventName,
      acquisition: readPublicAcquisitionContext(),
    }),
    credentials: "same-origin",
    keepalive: true,
  });
  if (!response.ok) throw new Error("public acquisition event rejected");
}

export async function recordUniqueLandingView(): Promise<void> {
  const { anonymousSessionId } = readPublicAcquisitionContext();
  const key = `sueopmachim:landing-viewed:v2:${anonymousSessionId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    // A storage-denied browser may record another view, but never loses access.
  }
  try {
    await recordPublicAcquisitionEvent("landing_viewed");
  } catch (error) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures.
    }
    throw error;
  }
}

export async function login(payload: {
  email: string;
  password: string;
}): Promise<{ state: WorkspaceState; csrfToken: string; role: Role }> {
  return request("/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let response: Response;
  const timeout = new AbortController();
  const timeoutId = window.setTimeout(() => timeout.abort(), 12_000);
  try {
    response = await fetch(url, { credentials: "same-origin", ...init, signal: init?.signal ?? timeout.signal });
  } catch {
    throw new ApiError(
      "NETWORK_ERROR",
      "서버에 닿지 못했어요. 입력은 이 기기에 남아 있습니다.",
      0,
      true,
      "network",
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
  const payload = (await response.json().catch(() => null)) as
    | T
    | ApiErrorShape
    | null;
  if (!response.ok) {
    const error = (payload as ApiErrorShape | null)?.error;
    throw new ApiError(
      error?.code ?? "REQUEST_FAILED",
      error?.message ?? "요청을 처리하지 못했어요.",
      response.status,
      error?.retryable ?? response.status >= 500,
      error?.requestId ?? response.headers.get("cf-ray") ?? "unknown",
      error?.fieldErrors ?? {},
    );
  }
  return payload as T;
}
